import { useMemo } from 'react';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import type { AccessCentrePermission } from 'domains/accessCentre/types';
import PermissionModuleCard from '../PermissionModuleCard';

const CARD_TITLE = 'Step 2: Assign Permissions';

const cardStyles = {
    width: '100%',
};

/** Group permissions by module name. */
const groupByModule = (permissions: AccessCentrePermission[]): Map<string, AccessCentrePermission[]> => {
    const map = new Map<string, AccessCentrePermission[]>();
    for (const p of permissions) {
        const list = map.get(p.module) ?? [];
        list.push(p);
        map.set(p.module, list);
    }
    return map;
};

export type AssignPermissionsCardProps = {
    permissions: AccessCentrePermission[];
    selectedPermissionIds: number[];
    onSelectionChange: (permissionIds: number[]) => void;
    isLoading?: boolean;
};

const AssignPermissionsCard = ({
    permissions,
    selectedPermissionIds,
    onSelectionChange,
    isLoading = false,
}: AssignPermissionsCardProps) => {
    const permissionsByModule = useMemo(() => groupByModule(permissions), [permissions]);
    const moduleKeys = useMemo(() => Array.from(permissionsByModule.keys()).sort(), [permissionsByModule]);

    const togglePermission = (id: number) => {
        const set = new Set(selectedPermissionIds);
        if (set.has(id)) {
            set.delete(id);
        } else {
            set.add(id);
            const permission = permissions.find((p) => p.id === id);
            if (permission?.depends_on?.length) {
                for (const key of permission.depends_on) {
                    const dep = permissions.find((p) => p.key === key);
                    if (dep) set.add(dep.id);
                }
            }
        }
        onSelectionChange(Array.from(set));
    };

    if (isLoading) {
        return (
            <Card variant="outlined" sx={cardStyles} data-testid="access-center-assign-permissions-card">
                <CardContent>
                    <Typography variant="h6" component="h2" gutterBottom>
                        {CARD_TITLE}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Loading permissions...
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="outlined" sx={cardStyles} data-testid="access-center-assign-permissions-card">
            <CardContent>
                <Typography variant="h6" component="h2" gutterBottom>
                    {CARD_TITLE}
                </Typography>
                <Stack spacing={0} sx={{ mt: 1 }}>
                    {moduleKeys.map((moduleKey) => {
                        const modulePermissions = permissionsByModule.get(moduleKey) ?? [];
                        return (
                            <PermissionModuleCard
                                key={moduleKey}
                                moduleKey={moduleKey}
                                permissions={modulePermissions}
                                selectedPermissionIds={selectedPermissionIds}
                                onTogglePermission={togglePermission}
                            />
                        );
                    })}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default AssignPermissionsCard;
